// ===== O desconto que o documento da proposta mostra =====
//
// Spec: docs/superpowers/specs/2026-10-07-desconto-no-documento.md
//
// O GERADOR NAO RECOMPOE (decisão do modelo novo: "os valores monetários devem chegar como números calculados pelo CRM.
// O HTML não recompõe preços, descontos, impostos ou total do pedido"). Ele imprime `item.descontoValor` e
// `proposta.desconto` como vierem. So que nenhum dado de producao os trazia: o construtor grava `desconto` = 0 e
// nenhum leitor produzia `descontoValor`. Medido em 07/10: uma proposta com 10% de desconto saia com "—" em cada linha
// e "Desconto total · já aplicado: R$ 0,00". Desde a troca do gerador (18/09).
//
// A CONTA MORA AQUI, num lugar so, e os dois caminhos de producao chamam `comDescontosCalculados`: o PDF do CRM
// (src/proposta.jsx) e o link do cliente (proposta-publica/index.ts). Calculado na LEITURA, a partir dos itens, nunca
// gravado: assim corrige tambem as propostas antigas, e o valor gravado nao tem como discordar do documento.
//
// SEM IMPORT NENHUM, pela mesma razao do documento e de proposta-do-banco.js: um `import` que o Deno nao resolva
// quebra a pagina do cliente em producao, e nao no teste. Por isso `centavos` e uma COPIA de `dinheiro` (src/dinheiro.js);
// tests/unit/proposta-desconto.test.js compara as duas em 840 combinacoes.

// Arredonda para centavo, meio centavo se AFASTANDO do zero. `Math.round(x * 100) / 100` come centavo
// (141.075 * 100 = 14107.499999999998): desloca-se a virgula pelo TEXTO do numero. Ver src/dinheiro.js.
function centavos(valor) {
  const n = typeof valor === 'number' ? valor : Number(valor);
  if (!Number.isFinite(n) || n === 0) return 0;
  const sinal = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const deslocado = Number(`${abs}e2`);
  if (!Number.isFinite(deslocado)) return sinal * (Math.round(abs * 100) / 100);
  const emCentavos = Math.round(deslocado);
  const devolta = Number(`${emCentavos}e-2`);
  return Number.isFinite(devolta) ? sinal * devolta : sinal * (emCentavos / 100);
}

/**
 * Desconto efetivo de um item, em percentual.
 *
 * O desconto global é um PISO que atinge todos os itens (Kaio, 17/09): um item com 5% próprio dentro de uma
 * proposta com 12% global sai com 12%, não com 17%. É a mesma regra da tela (src/proposta.jsx, `itensCalc`).
 */
export function descontoEfetivo(item, descontoGlobal) {
  return Math.max(Number(item?.pctDesconto) || 0, Number(descontoGlobal) || 0);
}

const bruto = (item) => (Number(item?.quantidade) || 0) * (Number(item?.precoUnitario) || 0);

/** O que o item custa depois do desconto efetivo. */
export function totalDaLinha(item, descontoGlobal) {
  return centavos(bruto(item) * (1 - descontoEfetivo(item, descontoGlobal) / 100));
}

/** Quantos reais de desconto o item levou. O modelo mostra valor, não percentual. */
export function descontoDaLinha(item, descontoGlobal) {
  return centavos(bruto(item) * (descontoEfetivo(item, descontoGlobal) / 100));
}

/**
 * A proposta como o documento precisa recebê-la: cada item com `descontoValor` e `totalLinha`, e `desconto` (o total
 * em reais) como soma das linhas JA arredondadas -- o mesmo critério do rodapé da tela, para o cliente somar na
 * calculadora e bater.
 *
 * `totalLinha` e recalculado em vez de lido de `preco_total`: a tela recalcula, e o documento tem de concordar com
 * a TELA (medido no staging em 07/10: 27 itens, 0 divergem do gravado, entao nada muda para o que ja existe). NAO mexe
 * em `total`, `totalProduto`, `frete`, ICMS-ST nem no percentual global: nada disso e desconto, e o que foi gravado e
 * o que o vendedor aprovou. Nao altera o objeto recebido.
 */
export function comDescontosCalculados(proposta) {
  const prop = proposta || {};
  const global = Number(prop.descontoGlobal) || 0;
  const itens = (Array.isArray(prop.itens) ? prop.itens : []).map((it) => ({
    ...it,
    descontoValor: descontoDaLinha(it, global),
    totalLinha: totalDaLinha(it, global),
  }));
  let desconto = 0;
  for (const it of itens) desconto += it.descontoValor;
  return { ...prop, itens, desconto: centavos(desconto) };
}
